import { useState } from "react";
import { message } from "antd";
import useRequest from "./useRequest";

const useAnalytics = () => {
  const [busesData, setBusesData] = useState(null);
  const [expensesData, setExpensesData] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [allDebtsData, setAllDebtsData] = useState(null);
  const [busPassengersData, setBusPassengersData] = useState(null);

  const [loading, setLoading] = useState({
    buses: false,
    expenses: false,
    timeline: false,
    debts: false,
    busPassengers: false,
  });

  const { request } = useRequest();

  /**
   * Obtener analytics de rentabilidad de buses
   * @param {string} startDate - Fecha inicio YYYY-MM-DD
   * @param {string} endDate - Fecha fin YYYY-MM-DD
   */
  async function getBusesAnalytics(startDate, endDate) {
    setLoading((prev) => ({ ...prev, buses: true }));

    return await request(
      "GET",
      `transfers/analytics/buses/?start_date=${startDate}&end_date=${endDate}`,
      (data) => {
        setBusesData(data);
        setLoading((prev) => ({ ...prev, buses: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, buses: false }));
        message.error("Error al cargar analytics de buses");
        return error;
      },
      {}
    );
  }

  /**
   * Obtener gastos por categoría
   * @param {string} startDate - Fecha inicio YYYY-MM-DD
   * @param {string} endDate - Fecha fin YYYY-MM-DD
   */
  async function getExpensesByCategory(startDate, endDate) {
    setLoading((prev) => ({ ...prev, expenses: true }));

    return await request(
      "GET",
      `transfers/analytics/expenses/?start_date=${startDate}&end_date=${endDate}`,
      (data) => {
        setExpensesData(data);
        setLoading((prev) => ({ ...prev, expenses: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, expenses: false }));
        message.error("Error al cargar gastos por categoría");
        return error;
      },
      {}
    );
  }

  /**
   * Obtener evolución temporal de ingresos vs gastos
   * @param {string} startDate - Fecha inicio YYYY-MM-DD
   * @param {string} endDate - Fecha fin YYYY-MM-DD
   */
  async function getIncomeExpensesTimeline(startDate, endDate) {
    setLoading((prev) => ({ ...prev, timeline: true }));

    return await request(
      "GET",
      `transfers/analytics/timeline/?start_date=${startDate}&end_date=${endDate}`,
      (data) => {
        setTimelineData(data);
        setLoading((prev) => ({ ...prev, timeline: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, timeline: false }));
        message.error("Error al cargar timeline de ingresos/gastos");
        return error;
      },
      {}
    );
  }

  /**
   * Obtener todas las deudas (vendedores, guías, coordinadores, transportistas, compañías)
   * Llama a cada tipo por separado y combina los resultados
   */
  async function getAllDebts() {
    setLoading((prev) => ({ ...prev, debts: true }));

    const userTypes = ["USER_SELLER", "USER_GUIDE", "USER_COORDINATOR", "USER_TRANSPORTER"];

    try {
      // Fetch debts for each user type in parallel
      const userDebtsPromises = userTypes.map((type) =>
        request(
          "GET",
          `transfers/all-debts/?type=${type}`,
          () => {},
          () => {},
          {}
        )
      );

      // Fetch company debts
      const companyDebtsPromise = request(
        "GET",
        `transfers/all-company-debts/`,
        () => {},
        () => {},
        {}
      );

      const results = await Promise.all([...userDebtsPromises, companyDebtsPromise]);

      // Extract results array from each response and combine
      const allDebts = results.flatMap((response) => response?.results || response || []);

      setAllDebtsData(allDebts);
      setLoading((prev) => ({ ...prev, debts: false }));
      return allDebts;
    } catch (error) {
      setLoading((prev) => ({ ...prev, debts: false }));
      message.error("Error al cargar deudas");
      return [];
    }
  }

  /**
   * Obtener pasajeros y pagos de un bus específico
   * @param {number} busId - ID del ScheduleExcursionBus
   */
  async function getBusPassengers(busId) {
    setLoading((prev) => ({ ...prev, busPassengers: true }));

    return await request(
      "GET",
      `transfers/analytics/buses/${busId}/passengers/`,
      (data) => {
        setBusPassengersData(data);
        setLoading((prev) => ({ ...prev, busPassengers: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, busPassengers: false }));
        message.error("Error al cargar pasajeros del bus");
        return error;
      },
      {}
    );
  }

  return {
    // Data
    busesData,
    expensesData,
    timelineData,
    allDebtsData,
    busPassengersData,
    // Loading states
    loading,
    // Functions
    getBusesAnalytics,
    getExpensesByCategory,
    getIncomeExpensesTimeline,
    getAllDebts,
    getBusPassengers,
  };
};

export default useAnalytics;
